export type MapPin = { id: string; number: string; x: number; y: number; space?: "image" | "legacy"; title?: string };
export type PdfPin = MapPin & { page: number };
export function pdfPinsFor(snags:Array<{id:string;reference:string;drawingId?:string|null;drawingPage?:number|null;markupX?:number|null;markupY?:number|null;pinCoordinateSpace?:'image'|'legacy'}>,drawingId:string):PdfPin[] {
  return snags.filter(snag=>snag.drawingId===drawingId && snag.markupX!=null && snag.markupY!=null).map(snag=>({id:snag.id,number:snagNumber(snag.reference),page:snag.drawingPage ?? 1,x:snag.markupX!/10000,y:snag.markupY!/10000,space:snag.pinCoordinateSpace ?? 'legacy'}));
}
export type PositionedPin = MapPin & { anchorX: number; anchorY: number; labelX: number; labelY: number; width: number; height: number };

export function snagNumber(reference: string): string {
  const digits = reference.match(/(\d+)$/)?.[1];
  return digits ? String(Number(digits)) : reference;
}

export function numberedSnags<T extends { id: string; reference: string }>(snags: T[]): T[] {
  return [...snags].sort((a, b) => snagNumber(a.reference).localeCompare(snagNumber(b.reference), "en", { numeric: true }) || a.reference.localeCompare(b.reference, "en", { numeric: true }) || a.id.localeCompare(b.id));
}

export function drawingRect(width: number, height: number, aspect: number) {
  if (!(width > 0 && height > 0 && aspect > 0)) return { left: 0, top: 0, width: 0, height: 0 };
  const fittedWidth = Math.min(width, height * aspect);
  const fittedHeight = fittedWidth / aspect;
  return { left: (width - fittedWidth) / 2, top: (height - fittedHeight) / 2, width: fittedWidth, height: fittedHeight };
}

export function imagePoint(x: number, y: number, width: number, height: number, aspect: number) {
  const rect = drawingRect(width, height, aspect);
  if (!rect.width || x < rect.left || x > rect.left + rect.width || y < rect.top || y > rect.top + rect.height) return null;
  return { x: (x - rect.left) / rect.width, y: (y - rect.top) / rect.height };
}

// Only the label moves to avoid overlap. Its anchor always remains at the saved
// position; a fine leader line makes the relationship explicit in screen/print.
export function layoutPins(pins: MapPin[], width: number, height: number, aspect: number): PositionedPin[] {
  const rect = drawingRect(width, height, aspect);
  const placed: PositionedPin[] = [];
  const valid = pins.filter((pin) => Number.isFinite(pin.x) && Number.isFinite(pin.y) && pin.x >= 0 && pin.x <= 1 && pin.y >= 0 && pin.y <= 1);
  const anchors = valid.map((pin) => ({ x: pin.space === "image" ? rect.left + pin.x * rect.width : pin.x * width, y: pin.space === "image" ? rect.top + pin.y * rect.height : pin.y * height }));
  for (const pin of [...valid].sort((a, b) => a.number.localeCompare(b.number, "en", { numeric: true }) || a.id.localeCompare(b.id))) {
    const anchorX = pin.space === "image" ? rect.left + pin.x * rect.width : pin.x * width;
    const anchorY = pin.space === "image" ? rect.top + pin.y * rect.height : pin.y * height;
    const w = Math.max(22, pin.number.length * 7 + 8), h = 22;
    const clampX = (x: number) => Math.max(w / 2, Math.min(width - w / 2, x));
    const clampY = (y: number) => Math.max(h / 2, Math.min(height - h / 2, y));
    let labelX = clampX(anchorX + w / 2 + 8), labelY = clampY(anchorY);
    const free = (x: number, y: number) => !anchors.some((anchor) => Math.abs(x - anchor.x) < w / 2 + 6 && Math.abs(y - anchor.y) < h / 2 + 6) && !placed.some((other) => Math.abs(x - other.labelX) < (w + other.width) / 2 + 3 && Math.abs(y - other.labelY) < (h + other.height) / 2 + 3);
    search: for (let radius = w / 2 + 8; radius < Math.hypot(width, height); radius += 26) {
      const count = Math.max(1, Math.ceil(2 * Math.PI * radius / 26));
      for (let i = 0; i < count; i += 1) {
        const x = clampX(anchorX + radius * Math.cos(i * 2 * Math.PI / count));
        const y = clampY(anchorY + radius * Math.sin(i * 2 * Math.PI / count));
        if (free(x, y)) { labelX = x; labelY = y; break search; }
      }
    }
    placed.push({ ...pin, anchorX, anchorY, labelX, labelY, width: w, height: h });
  }
  return placed;
}

export function drawingGroups<T extends { id: string; drawingId?: string | null; drawingPage?: number | null; markupX?: number | null; markupY?: number | null }>(snags: T[]) {
  const groups = new Map<string, { drawingId: string; page: number; snags: T[] }>();
  for (const snag of snags) {
    if (!snag.drawingId || snag.markupX == null || snag.markupY == null) continue;
    const page = snag.drawingPage ?? 1;
    const key = `${snag.drawingId}:${page}`;
    const group = groups.get(key) ?? { drawingId: snag.drawingId, page, snags: [] };
    group.snags.push(snag); groups.set(key, group);
  }
  return [...groups.values()];
}
